import Colors from '@digili-ui/constants/colors'
import { getIfpTranslationForState } from '@sec-loans-ui/common/steps/loan-catalog-step/loanCatalogStepUtils'
import { ApplicationDetails } from '@sec-loans-ui/common/steps/loan-catalog-step/types'
import {
  ApplicationSelectData,
  ApplicationType,
  SecLoanApplicationVisibleState,
  SecLoansApplicationVisibleMetadata,
} from '@sec-loans-ui/common/types'
import { getSecLoansLocalizedEnums } from '@sec-loans-ui/utils/getSecLoansLocalizedEnums'
import { assertValue } from '@shared/helpers'
import {
  ApplicantRole,
  IfpApplicationMetadata,
  IfpApplicationState,
  LoanOfferMetadata,
  LoanOfferState,
  SecLoanApplicationState,
} from '@shared/sec-loans'
import { formatFinnishCurrencyWithSymbol, getLanguage } from '@ui-common/utils'
import { BaseCardProps, Card, CardIconType } from '@ui-components/containers/Card'
import { useId } from 'react'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { resolveLoanOfferLabels } from '../../../../loan-offer/utils/helpers'

const RootCard = styled(Card)`
  > div {
    flex-wrap: nowrap;
  }
`

export const ApplicationCard = ({
  debtorUuid,
  applicationDetails,
  onApplicationSelect,
}: {
  debtorUuid?: string
  applicationDetails: ApplicationDetails
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  switch (applicationDetails.applicationType) {
    case ApplicationType.LoanApplication:
      return (
        <LoanApplicationCard
          applicationMetadata={applicationDetails.applicationMetadata}
          onApplicationSelect={onApplicationSelect}
        />
      )
    case ApplicationType.InstalmentFreePeriodApplication:
      return (
        <IfpApplicationCard
          debtorUuid={assertValue(debtorUuid, 'debtorUuid')}
          applicationMetadata={applicationDetails.applicationMetadata}
          onApplicationSelect={onApplicationSelect}
        />
      )
    case ApplicationType.LoanOffer:
      return (
        <LoanOfferCard
          applicationMetadata={applicationDetails.applicationMetadata}
          onApplicationSelect={onApplicationSelect}
        />
      )
  }
}

type BaseApplicationCardProps = {
  applicationId: string
  onApplicationSelect: () => void
  title: string
  subtitle: string
  description: string
  icon: BaseCardProps['icon']
  isClickable?: boolean
  isInactive: boolean
}

const BaseApplicationCard = ({
  applicationId,
  onApplicationSelect,
  title,
  subtitle,
  description,
  icon,
  isInactive,
  isClickable = true,
}: BaseApplicationCardProps) => {
  const idPrefix = `${useId()}_${applicationId}`
  const titleId = `${idPrefix}_card-title-id`
  const subtitleId = `${idPrefix}_card-subtitle-id`

  let iconBackgroundColor = Colors.BackgroundWeakPrimary
  let iconColor = Colors.ElementStrongPrimary

  if (isInactive) {
    iconBackgroundColor = Colors.BackgroundWeakNeutral
    iconColor = Colors.ElementMediumNeutral
  }

  return (
    <RootCard
      icon={icon}
      iconBackgroundColor={iconBackgroundColor}
      iconColor={iconColor}
      tagName="li"
      aria-labelledby={titleId}
      maxWidth={'max(500px, 50%)'}
      title={{
        text: title,
        id: titleId,
        $variant: 'heading',
        $tagName: 'h3',
      }}
      subtitle={{
        id: subtitleId,
        text: subtitle,
      }}
      key={applicationId}
      {...(isClickable
        ? {
            variant: 'primary',
            clickOptions: {
              kind: 'content',
              onClick: onApplicationSelect,
              ariaDescribedBy: `${titleId} ${subtitleId}`,
            },
          }
        : { variant: 'basic' })}
      description={description}
    />
  )
}

const LoanApplicationCard = ({
  applicationMetadata,
  onApplicationSelect,
}: {
  applicationMetadata: SecLoansApplicationVisibleMetadata
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  const { formatMessage } = useIntl()
  const { loanPurposeTypeLabelTexts, applicationTypeLabelTexts, applicationStateTexts } =
    getSecLoansLocalizedEnums(formatMessage)
  const selectData: ApplicationSelectData = {
    applicationId: applicationMetadata.applicationId,
    applicationType: ApplicationType.LoanApplication,
    data: {
      applicationPurpose: applicationMetadata.applicationPurpose,
      applicationType: applicationMetadata.applicationType,
    },
  }
  const isSecondaryApplicant = applicationMetadata.applicantRole === ApplicantRole.SecondaryApplicant

  const applicationStateIcon: Record<SecLoanApplicationVisibleState, CardIconType> = {
    Accepted: 'bankingPayslipIcon',
    Rejected: 'bankingPayslipIcon',
    CancelledByPrimaryApplicant: 'documentTextIcon',
    CancelledBySecondaryApplicant: 'documentTextIcon',
    Draft: 'documentEditIcon',
    ReturnedBySecondaryApplicant: 'documentEditIcon',
    Pending: 'documentHistoryIcon',
    Sending: 'documentHistoryIcon',
    WaitingForResend: 'documentHistoryIcon',
    WaitingForSecondaryApplicant: 'documentHistoryIcon',
  }

  const isClickable: Record<SecLoanApplicationState, boolean> = {
    Draft: true,
    CancelledByPrimaryApplicant: false,
    WaitingForSecondaryApplicant: true,
    ReturnedBySecondaryApplicant: !isSecondaryApplicant,
    CancelledBySecondaryApplicant: false,
    Sending: true,
    WaitingForResend: true,
    Accepted: true,
    Pending: true,
    Rejected: true,
    OfferReceived: true,
  }

  return (
    <BaseApplicationCard
      key={applicationMetadata.applicationId}
      icon={applicationStateIcon[applicationMetadata.applicationState]}
      onApplicationSelect={() => onApplicationSelect(selectData)}
      applicationId={applicationMetadata.applicationId}
      title={applicationTypeLabelTexts[applicationMetadata.applicationType]}
      subtitle={loanPurposeTypeLabelTexts[applicationMetadata.applicationPurpose]}
      description={applicationStateTexts[applicationMetadata.applicationState]}
      isClickable={isClickable[applicationMetadata.applicationState]}
      isInactive={!isClickable[applicationMetadata.applicationState]}
    />
  )
}

const IfpApplicationCard = ({
  debtorUuid,
  applicationMetadata,
  onApplicationSelect,
}: {
  debtorUuid: string
  applicationMetadata: IfpApplicationMetadata
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  const selectData: ApplicationSelectData = {
    applicationId: applicationMetadata.applicationId,
    applicationState: applicationMetadata.state,
    applicationType: ApplicationType.InstalmentFreePeriodApplication,
  }
  const isSecondaryApplicant = debtorUuid !== applicationMetadata.primaryUuid
  const { formatMessage } = useIntl()
  const lang = getLanguage()

  const applicationStateIcon: Record<IfpApplicationState, CardIconType> = {
    Accepted: 'bankingPayslipIcon',
    Rejected: 'bankingPayslipIcon',
    Cancelled: 'documentTextIcon',
    UnavailableOther: 'documentTextIcon',
    UnavailableDraftInvalidated: 'documentTextIcon',
    Draft: 'documentEditIcon',
    ReturnedByCoDebtor: 'documentEditIcon',
    Pending: 'documentHistoryIcon',
    Sending: 'documentHistoryIcon',
    WaitingForCoDebtor: 'documentHistoryIcon',
    WaitingForResend: 'documentHistoryIcon',
  }

  const isClickable: Record<IfpApplicationState, boolean> = {
    Accepted: false,
    Rejected: false,
    Cancelled: false,
    UnavailableOther: false,
    UnavailableDraftInvalidated: false,
    Draft: true,
    ReturnedByCoDebtor: !isSecondaryApplicant,
    Pending: true,
    Sending: true,
    WaitingForCoDebtor: true,
    WaitingForResend: true,
  }

  const isInactive: Record<IfpApplicationState, boolean> = {
    Accepted: false,
    Rejected: false,
    Cancelled: true,
    UnavailableOther: true,
    UnavailableDraftInvalidated: true,
    Draft: false,
    ReturnedByCoDebtor: false,
    Pending: false,
    Sending: false,
    WaitingForCoDebtor: false,
    WaitingForResend: false,
  }

  return (
    <BaseApplicationCard
      key={applicationMetadata.applicationId}
      icon={applicationStateIcon[applicationMetadata.state]}
      onApplicationSelect={() => onApplicationSelect(selectData)}
      applicationId={applicationMetadata.applicationId}
      title={formatMessage({ id: 'ifp-application-card-title' })}
      subtitle={`${applicationMetadata.loan.description[lang]} (${formatFinnishCurrencyWithSymbol(applicationMetadata.loan.balance)})`}
      description={getIfpTranslationForState(formatMessage, applicationMetadata, debtorUuid)}
      isClickable={isClickable[applicationMetadata.state]}
      isInactive={isInactive[applicationMetadata.state]}
    />
  )
}

const LoanOfferCard = ({
  applicationMetadata,
  onApplicationSelect,
}: {
  applicationMetadata: LoanOfferMetadata
  onApplicationSelect: (input: ApplicationSelectData) => void
}) => {
  const selectData: ApplicationSelectData = {
    id: applicationMetadata.id,
    applicationType: ApplicationType.LoanOffer,
  }
  const { formatMessage } = useIntl()
  const { loanType: firstLoanOfferType, isLoanTransfer: isFirstOfferLoanTransfer } = assertValue(
    applicationMetadata.offeredLoans[0],
    'applicationMetadata.offeredLoans[0]',
  )
  const { title, description: subtitle } = resolveLoanOfferLabels({
    offeredLoansAmount: applicationMetadata.offeredLoans.length,
    firstLoanOfferType,
    isFirstOfferLoanTransfer,
    formatMessage,
  })

  const applicationStateIcon: Record<LoanOfferState, CardIconType> = {
    OfferActive: 'bankingPayslipIcon',
    PricingExpired: 'bankingPayslipIcon',
    OfferExpired: 'bankingPayslipIcon',
  }

  return (
    <BaseApplicationCard
      key={applicationMetadata.id}
      icon={applicationStateIcon[applicationMetadata.state]}
      onApplicationSelect={() => onApplicationSelect(selectData)}
      applicationId={applicationMetadata.id}
      title={title}
      subtitle={subtitle}
      description={formatMessage({ id: 'loan-offer-card-description' })}
      isInactive={applicationMetadata.state === LoanOfferState.TermsExpired}
    />
  )
}
